import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Video, User, History, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { useWebSocket } from "@/context/WebSocketContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 30_000;

/**
 * RecentAdhocMeetings
 * -------------------
 * Compact "recent calls" list on the dashboard, showing the last few
 * ad-hoc video meetings this user was a part of (either side).
 *
 * Any meeting whose Daily.co room hasn't expired yet is *rejoinable* —
 * clicking Rejoin takes the user straight back into the same room.
 * This lets a therapist or facilitator recover from a dropped call in
 * one click, instead of asking the other party to re-invite.
 *
 * The `is_rejoinable` boolean is computed server-side so the client
 * doesn't have to reason about clock skew or timezone strings.
 */
function formatTimeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return "just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

function StatusBadge({ meeting }) {
  if (meeting.is_rejoinable) {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200">
        Active
      </Badge>
    );
  }
  if (meeting.status === "ended") {
    return <Badge variant="outline">Ended</Badge>;
  }
  return (
    <Badge variant="outline" className="text-muted-foreground">
      Expired
    </Badge>
  );
}

export function RecentAdhocMeetings({ limit = 8 }) {
  const { token, user } = useAuth();
  const { lastMessage } = useWebSocket();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const fetchRecent = useCallback(async () => {
    if (!token) return;
    try {
      const resp = await axios.get(
        `${API}/video/adhoc-meeting/recent?limit=${limit}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMeetings(resp.data.meetings || []);
    } catch (err) {
      // 401 handled globally by AuthContext.
    } finally {
      setLoading(false);
    }
  }, [token, limit]);

  useEffect(() => {
    if (!token || !user) return;
    fetchRecent();
    pollRef.current = setInterval(fetchRecent, POLL_INTERVAL_MS);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [token, user, fetchRecent]);

  // Refresh promptly on any ad-hoc-related WS event so the list is
  // never stale by more than a few seconds.
  useEffect(() => {
    if (!lastMessage) return;
    if (
      lastMessage.type === "adhoc_meeting_invite" ||
      lastMessage.type === "adhoc_meeting_ended"
    ) {
      fetchRecent();
    }
  }, [lastMessage, fetchRecent]);

  const handleRejoin = (meeting) => navigate(`/adhoc-meeting/${meeting.id}`);

  return (
    <Card data-testid="recent-adhoc-meetings-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
        <div>
          <CardTitle className="flex items-center gap-2 font-['Manrope'] text-lg">
            <History className="w-5 h-5" />
            Recent ad-hoc meetings
          </CardTitle>
          <CardDescription>
            Rejoin any meeting that's still active without a fresh invite.
          </CardDescription>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchRecent}
          disabled={loading}
          aria-label="Refresh"
          data-testid="recent-adhoc-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </CardHeader>
      <CardContent>
        {loading && meetings.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            Loading...
          </p>
        ) : meetings.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">
            No ad-hoc meetings yet.
          </p>
        ) : (
          <ul className="divide-y" data-testid="recent-adhoc-list">
            {meetings.map((m) => (
              <li
                key={m.id}
                className="flex items-center justify-between gap-3 py-2.5"
                data-testid={`recent-adhoc-row-${m.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <User className="w-4 h-4 shrink-0 text-muted-foreground" />
                    <span className="font-medium truncate">
                      {m.partner_name || "Unknown"}
                    </span>
                    {m.partner_role && (
                      <span className="text-xs text-muted-foreground">
                        ({m.partner_role})
                      </span>
                    )}
                    <StatusBadge meeting={m} />
                    <span className="text-xs text-muted-foreground">
                      · {m.is_initiator ? "you called" : "called you"} ·{" "}
                      {formatTimeAgo(m.created_at)}
                    </span>
                  </div>
                  {m.topic && m.topic !== "Ad-hoc meeting" && (
                    <div className="text-xs text-muted-foreground truncate ml-6 mt-0.5">
                      Topic: {m.topic}
                    </div>
                  )}
                </div>
                <Button
                  size="sm"
                  variant={m.is_rejoinable ? "default" : "outline"}
                  onClick={() => handleRejoin(m)}
                  disabled={!m.is_rejoinable}
                  className={
                    m.is_rejoinable
                      ? "bg-emerald-600 hover:bg-emerald-700"
                      : ""
                  }
                  data-testid={`recent-adhoc-rejoin-${m.id}`}
                >
                  <Video className="w-4 h-4 mr-2" />
                  {m.is_rejoinable ? "Rejoin" : "Ended"}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export default RecentAdhocMeetings;
